import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Award, TrendingUp, Target, Send, User, History, Calendar, Eye, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const QuickFeedback90 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [aplicadorNome, setAplicadorNome] = useState('');
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'kudos',
    para_usuario_id: '',
    competencia_tag: '',
    mensagem: '',
    anonimo: false,
    publico: false
  });

  useEffect(() => {
    carregarDadosIniciais();
  }, [user]);

  const carregarDadosIniciais = async () => {
    await carregarFuncionarios();
    await carregarNomeAplicador();
    await carregarHistorico();
  };

  const carregarNomeAplicador = async () => {
    try {
      if (!user) {
        setAplicadorNome('Usuário não identificado');
        return;
      }
      
      // 1. Primeiro tenta usar o nome que já vem no AuthContext
      if (user.name) {
        setAplicadorNome(user.name);
        return;
      }
      
      if (user.employee_name) {
        setAplicadorNome(user.employee_name);
        return;
      }
      
      // 2. Se não tem nome no user, busca usando employee_id
      if (user.employee_id) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/api/employees/${user.employee_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const nome = response.data.nome_completo || response.data.nome;
          if (nome) {
            setAplicadorNome(nome);
            return;
          }
        } catch (error) {
          console.log('Erro ao buscar por employee_id:', error);
        }
      }
      
      // 3. Fallback: busca pelo email do usuário
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const employees = response.data || [];
        const employeeFound = employees.find(emp => 
          emp.email === user.email || emp.user_email === user.email
        );
        
        if (employeeFound) {
          const nome = employeeFound.nome_completo || employeeFound.nome;
          setAplicadorNome(nome || user.email);
        } else {
          setAplicadorNome(user.email || 'Nome não disponível');
        }
      } catch (error) {
        console.error('Erro ao carregar nome do aplicador:', error);
        setAplicadorNome(user.email || 'Nome não disponível');
      }
    } catch (error) {
      console.error('Erro geral ao carregar nome do aplicador:', error);
      setAplicadorNome('Nome não disponível');
    }
  };

  const carregarFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const carregarHistorico = async () => {
    try {
      setLoadingHistorico(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback90/quick/historico`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorico(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setHistorico([]);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const enviarFeedback = async () => {
    if (!formData.para_usuario_id || !formData.mensagem) {
      toast.error('Selecione um funcionário e escreva uma mensagem');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/feedback90/quick`,
        {
          ...formData,
          de_usuario_id: user.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Feedback enviado com sucesso!');

      // Reset form
      setFormData({
        tipo: 'kudos',
        para_usuario_id: '',
        competencia_tag: '',
        mensagem: '',
        anonimo: false,
        publico: false
      });

      // Recarregar histórico
      await carregarHistorico();
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      toast.error('Erro ao enviar feedback. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const tipoOptions = [
    { value: 'kudos', label: 'Kudos (Parabéns)', icon: Award, color: 'yellow', description: 'Reconhecimento e celebração' },
    { value: 'keep_doing', label: 'Continue Fazendo', icon: TrendingUp, color: 'green', description: 'Comportamento positivo' },
    { value: 'improve', label: 'Oportunidade de Melhoria', icon: Target, color: 'blue', description: 'Feedback construtivo' }
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <MessageSquare className="h-8 w-8 mr-3 text-blue-600" />
          Feedback Rápido
        </h1>
        <p className="text-gray-600 mt-1">
          Envie feedback em segundos
        </p>
      </div>

      {/* Campo Aplicador */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Aplicador</p>
              <p className="text-lg font-semibold text-gray-900">{aplicadorNome || 'Carregando...'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs para Enviar e Histórico */}
      <Tabs defaultValue="enviar" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="enviar" className="flex items-center space-x-2">
            <Send className="h-4 w-4" />
            <span>Enviar Feedback</span>
          </TabsTrigger>
          <TabsTrigger value="historico" className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span>Histórico</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="enviar" className="space-y-6 mt-6">

      <Card>
        <CardHeader>
          <CardTitle>1. Escolha o Tipo de Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tipoOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = formData.tipo === option.value;
              return (
                <div
                  key={option.value}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? `border-${option.color}-500 bg-${option.color}-50`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData({ ...formData, tipo: option.value })}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <Icon className={`h-6 w-6 text-${option.color}-600`} />
                    <span className="font-semibold">{option.label}</span>
                  </div>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2. Para Quem?</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={formData.para_usuario_id}
            onChange={(e) => setFormData({ ...formData, para_usuario_id: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Selecione um funcionário</option>
            {funcionarios.filter(f => f.id !== user.id).map((func) => (
              <option key={func.id} value={func.id}>
                {func.nome}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>3. Sua Mensagem</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Competência (opcional)
            </label>
            <select
              value={formData.competencia_tag}
              onChange={(e) => setFormData({ ...formData, competencia_tag: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Selecione uma competência</option>
              <option value="comunicacao">Comunicação</option>
              <option value="lideranca">Liderança</option>
              <option value="trabalho_equipe">Trabalho em Equipe</option>
              <option value="resolucao_problemas">Resolução de Problemas</option>
              <option value="inovacao">Inovação</option>
              <option value="proatividade">Proatividade</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem *
            </label>
            <textarea
              value={formData.mensagem}
              onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
              placeholder="Escreva seu feedback aqui..."
              rows={5}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.mensagem.length} caracteres
            </p>
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.anonimo}
                onChange={(e) => setFormData({ ...formData, anonimo: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Enviar anonimamente</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.publico}
                onChange={(e) => setFormData({ ...formData, publico: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Tornar público (visível para todos)</span>
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-3">
        <Button variant="outline" onClick={() => navigate('/feedback/90')}>
          Cancelar
        </Button>
        <Button
          onClick={enviarFeedback}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Send className="h-4 w-4 mr-2" />
          {loading ? 'Enviando...' : 'Enviar Feedback'}
        </Button>
      </div>
        </TabsContent>

        {/* Aba de Histórico */}
        <TabsContent value="historico" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <History className="h-5 w-5 text-purple-600" />
                <span>Histórico de Quick Feedbacks</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHistorico ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Carregando histórico...</p>
                </div>
              ) : historico.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum feedback enviado ainda</h3>
                  <p className="text-gray-600">Os feedbacks que você enviar aparecerão aqui.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historico.map((feedback) => {
                    const tipoConfig = tipoOptions.find(t => t.value === feedback.tipo) || tipoOptions[0];
                    const Icon = tipoConfig.icon;
                    
                    return (
                      <div key={feedback.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 bg-${tipoConfig.color}-100 rounded-full flex items-center justify-center`}>
                              <Icon className={`h-5 w-5 text-${tipoConfig.color}-600`} />
                            </div>
                            <div>
                              <Badge variant="outline" className={`text-${tipoConfig.color}-700 border-${tipoConfig.color}-300`}>
                                {tipoConfig.label}
                              </Badge>
                              {feedback.competencia_tag && (
                                <Badge variant="secondary" className="ml-2">
                                  {feedback.competencia_tag}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-4 w-4" />
                              <span>
                                {feedback.criado_em 
                                  ? new Date(feedback.criado_em).toLocaleDateString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })
                                  : feedback.created_at
                                  ? new Date(feedback.created_at).toLocaleDateString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })
                                  : 'Data não disponível'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Aplicador:</strong> {feedback.de_usuario_nome || aplicadorNome || 'Nome não disponível'}
                          </p>
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Para:</strong> {feedback.para_usuario_nome || 'Nome não disponível'}
                          </p>
                          <p className="text-gray-700 mt-2">{feedback.mensagem}</p>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          {feedback.anonimo && (
                            <span className="flex items-center space-x-1">
                              <Eye className="h-3 w-3" />
                              <span>Anônimo</span>
                            </span>
                          )}
                          {feedback.publico && (
                            <span className="flex items-center space-x-1">
                              <Users className="h-3 w-3" />
                              <span>Público</span>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuickFeedback90;